import { ActionIcon, Box, Menu, Text } from '@mantine/core';
import { DataTableColumn } from 'mantine-datatable';
import React, { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { User } from 'src/admin/users/types';
import CreateUserModal from 'src/admin/users/components/CreateUserModal';
import { useDisclosure } from '@mantine/hooks';
import { useCreateUser } from 'src/admin/users/hooks/useCreateUser';
import { IconDotsVertical, IconTrash } from '@tabler/icons-react';
import DeleteUserModal from 'src/admin/users/components/DeleteUserModal';
import { useDeleteUser } from 'src/admin/users/hooks/useDeleteUser';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

export default function CompanyUsersPage() {
  return <CompanyWrapper contentComponent={CompanyUsersPageContent} />;
}

function CompanyUsersPageContent({ company }: { company: Company }) {
  const [opened, { open, close }] = useDisclosure(false);
  const { deleteUser } = useDeleteUser({ companyId: company?.id });
  const [userToDelete, setUserToDelete] = useState<null | User>(null);
  const { createUser, isLoading } = useCreateUser({ companyId: company?.id });

  const columns: DataTableColumn<User>[] = [
    {
      accessor: 'lastName',
      title: 'Last name',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.lastName}
        </Text>
      ),
    },
    {
      accessor: 'firstName',
      title: 'First name',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.firstName}
        </Text>
      ),
    },
    {
      accessor: 'emailAddress',
      title: 'Email address',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.emailAddress}
        </Text>
      ),
    },
    {
      accessor: 'userRole',
      title: 'User role',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.userRole}
        </Text>
      ),
    },
    {
      accessor: 'userStatus',
      title: 'User status',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.userStatus}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: record => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => setUserToDelete(record)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  const handleAddUser = () => {
    open();
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!userToDelete) return;
      await deleteUser(userToDelete.id);
      setUserToDelete(null);
      window.location.reload();
    } catch (e) {
      notifyAndLogError(
        'Failed to delete user',
        'An error occurred while deleting the user. Please try again.',
        e,
        { userId: userToDelete?.id },
      );
    }
  };

  return (
    <>
      <CreateUserModal
        opened={opened}
        close={close}
        createUserOverride={createUser}
        isLoading={isLoading}
      />
      <DeleteUserModal
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
        onDeleteConfirm={() => handleDeleteConfirm()}
      />
      <SimpleTable<User>
        resource={`admin/companies/${company.id}/users`}
        columns={columns}
        title="Users"
        primaryButton={{
          label: 'Add user',
          onClick: handleAddUser,
          variant: 'primary',
        }}
        showCount
        enableSearch
        pageSize={10}
        mode="client"
      />
    </>
  );
}
